import { Link } from 'react-router-dom';
import { DocSection, Callout } from '../../../pages/shell';
import { DocShot } from '../../DocShot';

export const toc = [
  { id: 'badge-meaning', label: 'Why a file shows a speech-problem badge' },
  { id: 're-transcribe', label: 'Re-transcribe now' },
  { id: 'repaired-copy', label: 'Create a repaired copy' },
  { id: 'pin-language', label: 'Language detection' },
  { id: 'check-transcripts', label: 'Check all transcripts' },
];

export default function Body() {
  return (
    <>
      <DocSection id="badge-meaning" title="Why a file shows a speech-problem badge">
        <p>
          A damaged or missing audio track doesn&rsquo;t fail an analysis job — ClipRadar marks
          the file with a speech-problem badge in the SPEECH column instead, so you know at a
          glance which files need a look rather than trusting an empty transcript that looks like
          a success.
        </p>
        <ul>
          <li><strong>Neutral</strong> — no audio track at all.</li>
          <li><strong>Amber</strong> — a transcript exists but is worth checking.</li>
          <li><strong>Red</strong> — the audio couldn&rsquo;t be transcribed.</li>
        </ul>
        <p>
          Click the badge to open a popover with the specific problem and your options:{' '}
          <strong>Re-transcribe now</strong>, <strong>Create repaired copy</strong>,{' '}
          <strong>View transcript</strong>, or <strong>Looks fine — dismiss</strong>. Before a
          file is ever flagged, ClipRadar already tries a bounded set of automatic retries with
          adjusted settings — a badge means those attempts weren&rsquo;t enough on their own.
        </p>
        <DocShot slug="fix-speech-problems" shot="badge" alt="A speech-problem badge on a Media row with its popover open" />
      </DocSection>

      <DocSection id="re-transcribe" title="Re-transcribe now">
        <p>
          Choose <strong>Re-transcribe now</strong> from the popover — or{' '}
          <strong>Re-analyze Speech</strong> from the file&rsquo;s row menu on{' '}
          <Link to="/docs/interface/media">Media</Link> — to run transcription again. Use it after
          you&rsquo;ve replaced a damaged file, or when the first attempt may have hit a transient
          issue.
        </p>
      </DocSection>

      <DocSection id="repaired-copy" title="Create a repaired copy">
        <p>
          For a file whose audio track is genuinely malformed, <strong>Create repaired
          copy</strong> makes a new copy of the file next to the original with audio ClipRadar can
          read cleanly. Your original file is left untouched — run analysis on the repaired copy
          to get a usable transcript.
        </p>
      </DocSection>

      <DocSection id="pin-language" title="Language detection">
        <p>
          ClipRadar detects each file&rsquo;s spoken language automatically — there&rsquo;s
          nothing to set for most footage. Bulgarian is the one special case: when a Bulgarian
          file is detected, the more accurate speech model is already downloaded, and your GPU has
          enough free VRAM, ClipRadar automatically upgrades that file to it. ClipRadar never
          downloads that model on its own to make this happen — it only uses it if it&rsquo;s
          already on disk.
        </p>
        <Callout kind="warn">
          TODO(founder): confirm whether a manual language override belongs on this page. Recent
          work removed the Settings control that let you pin a language by hand in favor of
          automatic per-file detection — this section currently documents only that automatic
          behavior.
        </Callout>
      </DocSection>

      <DocSection id="check-transcripts" title="Check all transcripts">
        <p>
          Rather than checking files one at a time, <strong>Settings &rarr; Models &rarr; Check
          transcripts</strong> runs a scan across your whole library — a quick health check, not a
          new transcription job — and flags any transcripts it doesn&rsquo;t trust. It jumps you
          straight to the Problems filter on <Link to="/docs/interface/media">Media</Link> so you
          can see exactly which files need attention.
        </p>
        <DocShot slug="fix-speech-problems" shot="check-transcripts" alt="Settings → Models with the Check transcripts control" />
      </DocSection>
    </>
  );
}
